import { createFileRoute, Link, useNavigate, useRouter } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { AuthInput, AuthLabel, ErrorMessage, PrimaryButton } from "@/components/auth/AuthShell";
import { BRAND } from "@/config";
import { WAIVER } from "@/content/waiver";
import { formatUsPhone } from "@/lib/auth";
import { useSignWaiver, waiverQuery } from "@/lib/family";

export const Route = createFileRoute("/_app/waiver")({
  component: WaiverPage,
});

function WaiverPage() {
  const { profile } = Route.useRouteContext();
  const navigate = useNavigate();
  const router = useRouter();
  const { data: signature, isLoading } = useQuery(waiverQuery(profile.id));
  const sign = useSignWaiver();
  const [name, setName] = useState([profile.first_name, profile.last_name].filter(Boolean).join(" "));
  const [agreed, setAgreed] = useState(false);
  const [phone, setPhone] = useState("");
  const [smsConsent, setSmsConsent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Google sign-ups arrive without a phone; ask for it here, like Triumph does.
  const needsPhone = !profile.phone?.trim();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (name.trim().length < 2) return setError("Please type your full name to sign.");
    if (needsPhone && phone.replace(/\D/g, "").length !== 10) return setError("Please enter a valid US phone number.");
    if (!agreed) return setError("Please confirm you agree to the terms above.");
    setError(null);
    try {
      await sign.mutateAsync({
        signedName: name,
        contact: needsPhone ? { userId: profile.id, phone, smsConsent } : undefined,
      });
      toast.success("Agreement signed. Thank you!");
      await router.invalidate();
      navigate({ to: "/home" });
    } catch (err) {
      console.error(err);
      setError("We couldn't save your signature. Please try again.");
    }
  }

  return (
    <section className="mx-auto max-w-2xl">
      {signature && (
        <Link to="/home" className="text-sm text-white/60 hover:text-white">
          ← Back
        </Link>
      )}
      <h1 className="mt-3 font-display text-3xl font-extrabold uppercase italic sm:text-4xl">Registration agreement</h1>
      {!signature && !isLoading && (
        <p className="mt-2 text-sm text-white/70">Please read carefully. You must accept these terms before registering any player.</p>
      )}

      <article className="mt-6 max-h-[55vh] space-y-4 overflow-y-auto rounded-md border border-white/10 bg-white/[0.03] p-5 text-sm leading-relaxed text-white/80">
        <h2 className="font-semibold text-white">{WAIVER.title}</h2>
        <p>{WAIVER.intro}</p>
        {WAIVER.sections.map((s) => (
          <p key={s.title}>
            <strong className="text-white">{s.title}</strong> {s.body}
          </p>
        ))}
      </article>

      {isLoading ? null : signature ? (
        <p className="mt-6 rounded-md border border-white/10 bg-white/5 px-4 py-3 text-sm text-white/80">
          You signed this agreement on {new Date(signature.signed_at).toLocaleDateString("en-US", { dateStyle: "long" })}.
        </p>
      ) : (
        <form onSubmit={handleSubmit} className="mt-6 space-y-4" noValidate>
          <div>
            <AuthLabel htmlFor="signed_name">Type your full legal name to sign</AuthLabel>
            <AuthInput id="signed_name" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
          </div>
          {needsPhone && (
            <>
              <div>
                <AuthLabel htmlFor="phone">Mobile phone</AuthLabel>
                <AuthInput
                  id="phone"
                  type="tel"
                  inputMode="tel"
                  value={phone}
                  onChange={(e) => setPhone(formatUsPhone(e.target.value))}
                  placeholder="(555) 555-5555"
                  autoComplete="tel"
                />
              </div>
              <label className="flex cursor-pointer items-start gap-2 text-xs leading-snug text-white/70">
                <input type="checkbox" className="mt-0.5 accent-a1-red" checked={smsConsent} onChange={(e) => setSmsConsent(e.target.checked)} />
                <span>
                  I agree to receive text messages from {BRAND.name} about schedules, programs and account updates.
                  Consent is not a condition of enrollment. Message frequency varies; message &amp; data rates may apply.
                  Reply STOP to unsubscribe, HELP for help.
                </span>
              </label>
            </>
          )}
          <label className="flex cursor-pointer items-start gap-2 text-sm text-white/80">
            <input type="checkbox" className="mt-1 accent-a1-red" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
            <span>I have read and agree to this agreement on behalf of my player(s).</span>
          </label>
          <ErrorMessage message={error} />
          <PrimaryButton type="submit" disabled={sign.isPending}>
            {sign.isPending ? "Signing…" : "I agree and continue"}
          </PrimaryButton>
        </form>
      )}
    </section>
  );
}
